/** Create / edit asset dialog with type-driven attribute fields. */
import { useNavigate } from "@tanstack/react-router";
import { type ReactElement, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import {
  type Asset,
  type AssetIn,
  type AssetPatch,
  CRITICALITIES,
  useAssetTypes,
  useAsset,
  useCreateAsset,
  useUpdateAsset,
} from "@/features/assets/queries";
import {
  AttributeFields,
  type AttributeSchemaEntry,
  type AttributeValues,
  compactAttributes,
  withDefaults,
} from "@/shared/components/attribute-fields";
import { FormDialog } from "@/shared/components/form-dialog";
import {
  AssetSelect,
  AssetTypePicker,
  LocationPicker,
  ModelPicker,
  PartyPicker,
  SitePicker,
  EntityPicker,
} from "@/shared/components/pickers";
import { nullable, numberOrNull, useForm } from "@/shared/lib/form";
import { Field, Input, Select, Textarea } from "@/shared/ui/form";
import { api } from "@/api/client";
import { unwrap } from "@/api/problem";

/** Form values (strings for inputs; converted on submit). */
interface AssetFormValues extends Record<string, unknown> {
  code: string;
  name: string;
  type_id: string;
  parent_id: string;
  position: string;
  serial_number: string;
  model_id: string;
  manufacturer_id: string;
  owner_id: string;
  operating_unit_id: string;
  maintenance_team_id: string;
  cost_center_id: string;
  initial_meter_value: string;
  site_id: string;
  location_id: string;
  criticality: string;
  commissioned_on: string;
  purchased_on: string;
  purchase_cost: string;
  purchase_currency: string;
  warranty_until: string;
  description: string;
  tags: string;
}

const schema = z.object({
  name: z.string().trim().min(1, "Required"),
  type_id: z.string().min(1, "Required"),
});

/** Convert an existing asset into form values. */
export function assetToValues(
  a: Asset | undefined,
  defaults: Partial<AssetFormValues> = {},
): AssetFormValues {
  return {
    code: a?.code ?? "",
    name: a?.name ?? "",
    type_id: a?.type.id ?? "",
    parent_id: a?.parent_id ?? "",
    position: a?.position ?? "",
    serial_number: a?.serial_number ?? "",
    model_id: a?.model?.id ?? "",
    manufacturer_id: a?.manufacturer?.id ?? "",
    owner_id: a?.owner?.id ?? "",
    operating_unit_id: a?.operating_unit?.id ?? "",
    maintenance_team_id: a?.maintenance_team?.id ?? "",
    cost_center_id: a?.cost_center?.id ?? "",
    initial_meter_value: "",
    site_id: a?.site?.id ?? "",
    location_id: a?.location?.id ?? "",
    criticality: a?.criticality ?? "medium",
    commissioned_on: a?.commissioned_on ?? "",
    purchased_on: a?.purchased_on ?? "",
    purchase_cost: a?.purchase_cost?.amount ?? "",
    purchase_currency: a?.purchase_cost?.currency ?? "",
    warranty_until: a?.warranty_until ?? "",
    description: a?.description ?? "",
    tags: a?.tags.join(", ") ?? "",
    ...defaults,
  };
}

/** Build the create payload. */
export function toCreatePayload(v: AssetFormValues, attributes: AttributeValues): AssetIn {
  return {
    code: nullable(v.code),
    name: v.name.trim(),
    type_id: v.type_id,
    parent_id: nullable(v.parent_id),
    position: v.position,
    serial_number: v.serial_number,
    model_id: nullable(v.model_id),
    manufacturer_id: nullable(v.manufacturer_id),
    owner_id: nullable(v.owner_id),
    operating_unit_id: nullable(v.operating_unit_id),
    maintenance_team_id: nullable(v.maintenance_team_id),
    cost_center_id: nullable(v.cost_center_id),
    initial_meter_value: numberOrNull(v.initial_meter_value),
    site_id: nullable(v.site_id),
    location_id: nullable(v.location_id),
    status: "active",
    criticality: v.criticality,
    commissioned_on: nullable(v.commissioned_on),
    purchased_on: nullable(v.purchased_on),
    purchase_cost: numberOrNull(v.purchase_cost),
    purchase_currency: v.purchase_currency || "",
    warranty_until: nullable(v.warranty_until),
    description: v.description,
    attributes,
    tags: v.tags
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean),
  };
}

/** Build the patch payload (everything editable; the API ignores unchanged values). */
export function toPatchPayload(v: AssetFormValues, attributes: AttributeValues): AssetPatch {
  const full: Partial<AssetIn> = toCreatePayload(v, attributes);
  delete full.type_id;
  delete full.code;
  delete full.parent_id;
  delete full.status;
  delete full.meter_at_install;
  delete full.initial_meter_value;
  return full;
}

/** Props for `AssetFormDialog`. */
export interface AssetFormDialogProps {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  /** Existing asset → edit mode. */
  asset?: Asset;
  /** Pre-fill for "add component" flows. */
  defaults?: Partial<AssetFormValues>;
  /** Limit component creation to types compatible with the selected host. */
  parentTypeId?: string;
}

/** Asset create/edit dialog. */
export function AssetFormDialog({
  open,
  onOpenChange,
  asset,
  defaults,
  parentTypeId,
}: AssetFormDialogProps): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const types = useAssetTypes();
  const create = useCreateAsset();
  const update = useUpdateAsset(asset?.id ?? "");
  const initial = useMemo(() => assetToValues(asset, defaults), [asset, defaults]);
  const form = useForm<AssetFormValues>({ initial, schema, idPrefix: "asset" });
  const [attributes, setAttributes] = useState<AttributeValues>(asset?.attributes ?? {});
  const parent = useAsset(form.values.parent_id);
  const inherited = Boolean(form.values.parent_id);

  useEffect(() => {
    if (parent.data && inherited) {
      form.patch({
        site_id: parent.data.site?.id ?? "",
        location_id: parent.data.location?.id ?? "",
      });
    }
  }, [parent.data, inherited]);

  const selectedType = types.data?.find((ty) => ty.id === form.values.type_id);
  const definitions = (selectedType?.attribute_schema ?? []) as unknown as AttributeSchemaEntry[];

  // Apply defaults whenever the type (and therefore the schema) changes.
  useEffect(() => {
    setAttributes((current) => withDefaults(definitions, current));
  }, [selectedType?.id]);

  const attributeErrors = Object.fromEntries(
    Object.entries(form.errors).filter(([k]) => definitions.some((d) => d.key === k)),
  ) as Record<string, string>;

  const submit = async (): Promise<void> => {
    const ok = await form.submit(async (v) => {
      const attrs = compactAttributes(definitions, attributes);
      if (asset) {
        await update.mutateAsync(toPatchPayload(v, attrs));
      } else {
        const created = await create.mutateAsync(toCreatePayload(v, attrs));
        await navigate({ to: "/assets/$assetId", params: { assetId: created.id } });
      }
    });
    if (ok) {
      onOpenChange(false);
    }
  };

  const isComponent = selectedType?.is_component ?? false;

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={asset ? t("assets.edit") : t("assets.new")}
      onSubmit={submit}
      submitting={form.submitting}
      submitLabel={asset ? t("app.save") : t("app.create")}
      error={form.errors._}
      wide
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field id="asset-type_id" label={t("app.type")} error={form.errors.type_id}>
          <AssetTypePicker
            id="asset-type_id"
            value={form.values.type_id}
            onChange={(id) => {
              form.set("type_id", id ?? "");
              if (!defaults?.parent_id) form.set("parent_id", "");
            }}
            parentTypeId={parentTypeId}
            disabled={!!asset}
            required
            aria-invalid={!!form.errors.type_id}
          />
        </Field>
        <Field id="asset-name" label={t("app.name")} error={form.errors.name}>
          <Input {...form.field("name")} required />
        </Field>
        {!asset ? (
          <Field
            id="asset-code"
            label={`${t("app.code")} (${t("app.optional")})`}
            error={form.errors.code}
            hint={t("assets.codeHint")}
          >
            <Input {...form.field("code")} />
          </Field>
        ) : null}
        <Field
          id="asset-serial_number"
          label={t("assets.serial")}
          error={form.errors.serial_number}
        >
          <Input {...form.field("serial_number")} />
        </Field>
        {isComponent && !asset ? (
          <>
            <Field id="asset-parent_id" label={t("assets.parent")} error={form.errors.parent_id}>
              <AssetSelect
                id="asset-parent_id"
                value={form.values.parent_id}
                onChange={(id) => form.set("parent_id", id ?? "")}
                installTypeId={form.values.type_id || undefined}
                disabled={Boolean(defaults?.parent_id)}
              />
            </Field>
            <Field id="asset-position" label={t("assets.positionOnParent")}>
              <Input {...form.field("position")} placeholder="port / starboard / centre" />
            </Field>
          </>
        ) : null}
        <Field
          id="asset-manufacturer_id"
          label={t("assets.manufacturer")}
          error={form.errors.manufacturer_id}
        >
          <PartyPicker
            combobox
            selectedLabel={
              form.values.manufacturer_id === asset?.manufacturer?.id
                ? asset?.manufacturer?.name
                : undefined
            }
            id="asset-manufacturer_id"
            role="manufacturer"
            aria-invalid={Boolean(form.errors.manufacturer_id)}
            value={form.values.manufacturer_id}
            onChange={(id) => form.set("manufacturer_id", id ?? "")}
          />
        </Field>
        <Field
          id="asset-operating_unit_id"
          label={t("assets.operatingUnit")}
          error={form.errors.operating_unit_id}
        >
          <PartyPicker
            combobox
            selectedLabel={
              form.values.operating_unit_id === asset?.operating_unit?.id
                ? asset?.operating_unit?.name
                : undefined
            }
            id="asset-operating_unit_id"
            role="operator"
            aria-invalid={Boolean(form.errors.operating_unit_id)}
            value={form.values.operating_unit_id}
            onChange={(id) => {
              form.set("operating_unit_id", id ?? "");
              form.set("cost_center_id", "");
            }}
          />
        </Field>
        <Field id="asset-maintenance_team_id" label={t("assets.maintenanceTeam")}>
          <EntityPicker
            id="asset-maintenance_team_id"
            value={form.values.maintenance_team_id}
            onChange={(id) => form.set("maintenance_team_id", id ?? "")}
            queryKey={["teams"]}
            load={async () =>
              unwrap(await api.GET("/api/v1/identity/teams")).map((team) => ({
                id: team.id,
                label: team.name,
              }))
            }
          />
        </Field>
        <Field id="asset-cost_center_id" label={t("assets.costCenter")}>
          <EntityPicker
            id="asset-cost_center_id"
            value={form.values.cost_center_id}
            onChange={(id) => form.set("cost_center_id", id ?? "")}
            queryKey={["cost-centers", form.values.operating_unit_id]}
            load={async () =>
              unwrap(
                await api.GET("/api/v1/parties/cost-centers", {
                  params: {
                    query: { operating_unit_id: form.values.operating_unit_id || undefined },
                  },
                }),
              ).items.map((center) => ({ id: center.id, label: `${center.code} · ${center.name}` }))
            }
          />
        </Field>
        {!asset && selectedType?.meter_templates.some((meter) => meter.is_primary) ? (
          <Field
            id="asset-initial_meter_value"
            label={t("assets.initialReading")}
            error={form.errors.initial_meter_value}
          >
            <Input {...form.field("initial_meter_value")} type="number" min="0" step="any" />
          </Field>
        ) : null}
        <Field id="asset-model_id" label={t("assets.model")}>
          <ModelPicker
            id="asset-model_id"
            assetTypeId={form.values.type_id || null}
            value={form.values.model_id}
            onChange={(id) => form.set("model_id", id ?? "")}
          />
        </Field>
        <Field id="asset-owner_id" label={t("assets.owner")} hint={t("assets.ownerHint")}>
          <PartyPicker
            id="asset-owner_id"
            role="customer"
            value={form.values.owner_id}
            onChange={(id) => form.set("owner_id", id ?? "")}
          />
        </Field>
        <Field id="asset-criticality" label={t("assets.criticality")}>
          <Select
            id="asset-criticality"
            value={form.values.criticality}
            onChange={(e) => form.set("criticality", e.target.value)}
          >
            {CRITICALITIES.map((c) => (
              <option key={c} value={c}>
                {t(`assets.criticalityLevel.${c}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          id="asset-site_id"
          label={t("app.site")}
          error={form.errors.site_id}
          hint={inherited ? t("feedbackAssets.inheritedLocation") : undefined}
        >
          {inherited ? (
            <Input
              id="asset-site_id"
              readOnly
              value={parent.data?.site?.name ?? asset?.site?.name ?? ""}
            />
          ) : (
            <SitePicker
              id="asset-site_id"
              value={form.values.site_id}
              onChange={(id) => form.patch({ site_id: id ?? "", location_id: "" })}
            />
          )}
        </Field>
        <Field id="asset-location_id" label={t("assets.location")} error={form.errors.location_id}>
          {inherited ? (
            <Input
              id="asset-location_id"
              readOnly
              value={parent.data?.location?.name ?? asset?.location?.name ?? ""}
            />
          ) : (
            <LocationPicker
              id="asset-location_id"
              siteId={form.values.site_id || null}
              value={form.values.location_id}
              onChange={(id) => form.set("location_id", id ?? "")}
              disabled={!form.values.site_id}
            />
          )}
        </Field>
        <Field
          id="asset-commissioned_on"
          label={t("assets.commissioned")}
          error={form.errors.commissioned_on}
        >
          <Input type="date" {...form.field("commissioned_on")} />
        </Field>
        <Field
          id="asset-purchased_on"
          label={t("assets.purchased")}
          error={form.errors.purchased_on}
        >
          <Input type="date" {...form.field("purchased_on")} />
        </Field>
        <Field
          id="asset-purchase_cost"
          label={t("assets.purchaseCost")}
          error={form.errors.purchase_cost}
        >
          <div className="flex gap-2">
            <Input type="number" step="any" min="0" {...form.field("purchase_cost")} />
            <Input
              className="w-24"
              placeholder="SAR"
              maxLength={3}
              {...form.field("purchase_currency")}
              aria-label={t("assets.currency")}
            />
          </div>
        </Field>
        <Field
          id="asset-warranty_until"
          label={t("assets.warrantyUntil")}
          error={form.errors.warranty_until}
        >
          <Input type="date" {...form.field("warranty_until")} />
        </Field>
        <Field id="asset-tags" label={t("app.tags")} hint={t("app.tagsHint")}>
          <Input {...form.field("tags")} />
        </Field>
      </div>
      <Field id="asset-description" label={t("app.description")}>
        <Textarea {...form.field("description")} />
      </Field>
      {definitions.length > 0 ? (
        <div className="rounded-md border p-3">
          <h3 className="mb-3 text-sm font-semibold">{t("assets.attributes")}</h3>
          <AttributeFields
            definitions={definitions}
            values={attributes}
            onChange={setAttributes}
            errors={attributeErrors}
          />
        </div>
      ) : null}
    </FormDialog>
  );
}
